import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useEffect } from 'react';

import { showResult } from '../../utils';
import { apiUrlGenerator } from '../../use-api';

import type {
  ICcmOnboardingStatusReportFilters,
  ICcmOnboardingStatusResponse,
  ICcmOnboardingStatusResponseData,
} from 'infrastructure/interfaces';

const url = `reports/ccm/reports/onboarding-status`;

interface IFilterData
  extends Partial<
    Omit<
      ICcmOnboardingStatusReportFilters,
      'clinicGuid' | 'patientGuid' | 'ccmStatus'
    >
  > {
  clinicGuid?: string;
  ccmStatus?: string;
  guid?: string;
}

const queryFn = async (filter: IFilterData) => {
  const { data, error } =
    await dataService.getList<ICcmOnboardingStatusResponseData>(
      apiUrlGenerator(url, filter),
    );

  if (error) {
    return Promise.reject(error);
  }

  if (data) {
    return {
      statuses: {
        ...data.onboardingStatuses,
        'Total CCM Patients': data.totalOnboardingPatients,
        Active: data.enrolledPatientsCount,
      },
      insurances: data.patientsByInsurances,
      total: data.totalOnboardingPatients,
      active: data.enrolledPatientsCount,
    };
  }

  return data;
};

const getOnboardingStatusKey = (filter: IFilterData) => [
  'getCcmOnboardingStatus',
  filter,
];

export const useOnboardingStatus = (
  props: ICcmOnboardingStatusReportFilters,
) => {
  const filter: IFilterData = {
    doctorGuid: props.doctorGuid,
    childClinic: props.clinicGuid?.length ? props.childClinic : undefined,
    clinicGuid: props.clinicGuid?.join(','),
    pcmStatus: props.pcmStatus,
  };

  const { data, error, isLoading, isFetching } = useQuery<
    ICcmOnboardingStatusResponse | undefined,
    string | undefined
  >({
    queryKey: getOnboardingStatusKey(filter),
    queryFn: async () => queryFn(filter),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  return { data, isLoading: isLoading || isFetching };
};
